import { Fragment, useContext } from 'react';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json';

/**
 * EntityLinkList — rend une liste de LIENS à partir de deux tableaux PARALLÈLES
 * `ids` et `labels` (même ordre : ex. `contacts` (nids) ↔ `contact_names` (noms)
 * d'un engagement). Le « zip » de deux tableaux parallèles n'est pas exprimable
 * en YAML reactive-json (un `Switch` n'itère qu'UN tableau, sans accès croisé au
 * tableau frère par index), d'où ce mini-plugin générique READ-ONLY. Réutilisable
 * pour toute liste d'entités liées (participants, contacts, sociétés…).
 *
 * props :
 *   - ids: array         — identifiants ; l'URL = hrefPrefix + id
 *   - labels: array      — libellés affichés (parallèle à ids)
 *   - hrefPrefix: string — ex. "/crm/contacts/"
 *   - linkClass: string  — classes du lien (défaut : lien iris DS)
 *   - separator: string  — séparateur entre items (défaut ", ")
 *   - emptyText: string  — texte si aucun label (sinon rend null)
 *
 * Robustesse : si un id manque (moins d'ids que de labels), le libellé est rendu
 * en TEXTE simple — jamais de lien cassé.
 */

const ev = (gdc, tc) => (v) => {
  try {
    return evaluateTemplateValue({ valueToEvaluate: v, globalDataContext: gdc, templateContext: tc });
  } catch {
    return undefined;
  }
};

const toArray = (v) => (Array.isArray(v) ? v : v == null || v === '' ? [] : [v]);

/**
 * Zippe deux tableaux parallèles en descripteurs de lien { label, href }.
 * `href` = null quand l'id correspondant manque (→ rendu en texte, pas de lien
 * cassé). Fonction PURE : c'est ici qu'est toute la logique (testable sans rendu).
 */
export function entityLinks(ids, labels, hrefPrefix = '') {
  const L = toArray(labels);
  const I = toArray(ids);
  return L.map((label, i) => {
    const id = I[i];
    const linkable = id != null && id !== '';
    return { label, href: linkable ? `${hrefPrefix}${id}` : null };
  });
}

export function EntityLinkList({ props = {} }) {
  const e = ev(useContext(GlobalDataContext), useContext(TemplateContext));
  const ids = toArray(e(props.ids));
  const labels = toArray(e(props.labels));
  const hrefPrefix = e(props.hrefPrefix) ?? '';
  const linkClass = e(props.linkClass) ?? 'text-iris-600 hover:underline';
  const separator = e(props.separator) ?? ', ';
  const emptyText = e(props.emptyText);

  if (!labels.length) {
    return emptyText ? <span className="text-slate-400">{emptyText}</span> : null;
  }

  return (
    <span>
      {entityLinks(ids, labels, hrefPrefix).map(({ label, href }, i) => (
        <Fragment key={`${href ?? 'x'}-${i}`}>
          {i > 0 ? separator : null}
          {href ? (
            <a href={href} className={linkClass}>{label}</a>
          ) : (
            <span className="text-slate-700">{label}</span>
          )}
        </Fragment>
      ))}
    </span>
  );
}

export default EntityLinkList;
